import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { Crown, Check, CreditCard, Shield, Zap, ArrowRight, Sparkles, Video, Package, Star, Loader2 } from 'lucide-react';

// Gold tier benefits
const goldBenefits = [
  { icon: Zap, text: 'Unlimited listings' },
  { icon: Shield, text: 'Verified seller badge' },
  { icon: CreditCard, text: '0% seller commission' },
  { icon: Check, text: 'AI card scanner access' },
  { icon: Check, text: 'Direct messaging' },
];

// Elite tier benefits (includes all Gold benefits plus)
const eliteBenefits = [
  { icon: Video, text: 'Host Live Auctions', highlight: true },
  { icon: Package, text: 'Combined Shipping ($5 + $0.50/item)', highlight: true },
  { icon: Shield, text: 'Shipping Subsidy Protection', highlight: true },
  { icon: Sparkles, text: 'Electric Blue Elite Badge', highlight: true },
  { icon: Star, text: 'Priority support' },
];

export const SubscriptionPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState('gold');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    // Check URL for tier preference
    const tierParam = searchParams.get('tier');
    if (tierParam === 'elite') {
      setSelectedTier('elite');
    }
  }, [searchParams]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await api.post('/api/subscription/portal');
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(error.response?.data?.detail || 'Unable to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }

    if (user?.subscription_active) {
      // Check if upgrading
      if (user?.membership_status === 'gold' && tier === 'elite') {
        toast.info('Upgrade to Elite to unlock live auctions!');
      } else {
        toast.info('You already have an active subscription');
        navigate('/dashboard');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await api().post('/subscription/create-checkout', {
        frontend_origin: window.location.origin,
        plan_type: 'monthly',
        tier: tier,
        success_url: `${window.location.origin}/dashboard?subscription=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/subscription?cancelled=true`
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start subscription');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get badge for current membership
  const getMembershipBadge = () => {
    if (user?.membership_status === 'elite') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold" 
              style={{ backgroundColor: 'rgba(0, 191, 255, 0.2)', color: '#00BFFF' }}>
          <Sparkles className="w-3 h-3" />
          ELITE
        </span>
      );
    } else if (user?.membership_status === 'gold') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold"
              style={{ backgroundColor: 'rgba(255, 215, 0, 0.2)', color: '#FFD700' }}>
          <Crown className="w-3 h-3" />
          GOLD
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-6">
              <Crown className="w-12 h-12 text-amber-500" />
              <Sparkles className="w-12 h-12 text-cyan-400" />
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl uppercase text-white">
              Seller Membership
            </h1>
            <p className="text-zinc-400 mt-4 text-lg">
              Choose your tier. Unlock powerful selling features.
            </p>
          </div>

          {user?.subscription_active ? (
            <div className="max-w-md mx-auto stat-card p-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
                {getMembershipBadge()}
              </div>
              <p className="text-emerald-400 font-medium text-xl mb-2">You're subscribed!</p>
              <p className="text-zinc-400 mb-2">
                Tier: <span className="text-white font-medium capitalize">{user.membership_status || 'Gold'}</span>
              </p>
              <p className="text-zinc-500 text-sm mb-6">
                Expires: {user.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : 'Active'}
              </p>
              
              {user?.membership_status === 'gold' && (
                <div className="mb-6 p-4 rounded-lg border border-zinc-700 bg-zinc-800/50">
                  <p className="text-zinc-400 font-medium mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-400/50" />
                    Elite Tier - Coming Soon
                  </p>
                  <p className="text-zinc-500 text-sm mb-4">
                    Live Auctions feature is under development. We're building dedicated servers for streaming.
                  </p>
                  <div className="w-full h-10 rounded-lg bg-zinc-700/50 border border-zinc-600 flex items-center justify-center">
                    <span className="text-zinc-400 text-sm">Stay Tuned!</span>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate('/dashboard')} 
                  className="btn-primary"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Seller Dashboard
                </Button>
                <Button 
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800"
                  data-testid="manage-subscription-btn"
                >
                  {portalLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Manage Subscription'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Gold Plan */}
              <div 
                className={`stat-card p-8 relative cursor-pointer transition-all ${
                  selectedTier === 'gold' 
                    ? 'border-amber-500/50 bg-amber-500/5' 
                    : 'border-white/10 hover:border-amber-500/30'
                }`}
                onClick={() => setSelectedTier('gold')}
                data-testid="gold-tier-card"
              >
                <div className="relative z-10">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Crown className="w-8 h-8 text-amber-500" />
                    <h3 className="font-heading font-bold text-2xl text-white">Gold</h3>
                  </div>
                  <p className="text-zinc-500 text-sm text-center mb-6">Perfect for getting started</p>
                  
                  <div className="text-center mb-6">
                    <p className="font-mono text-5xl font-bold text-amber-400">
                      $20<span className="text-xl text-zinc-500">/mo</span>
                    </p>
                    <p className="text-zinc-500 text-sm mt-2">Billed monthly</p>
                  </div>
                  
                  {/* SLAB50 Coupon Hint */}
                  <div className="mb-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-amber-400 text-sm text-center">
                      Use code <span className="font-mono font-bold">SLAB50</span> for 50% off!
                    </p>
                  </div>

                  <div className="space-y-3 mb-8">
                    {goldBenefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <Check className="w-3 h-3 text-amber-400" />
                        </div>
                        <span className="text-zinc-300 text-sm">{benefit.text}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleSubscribe('gold')}
                    disabled={loading}
                    className={`w-full h-12 ${selectedTier === 'gold' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-zinc-700 hover:bg-zinc-600'}`}
                    data-testid="gold-subscribe-btn"
                  >
                    {loading && selectedTier === 'gold' ? <LoadingSpinner size="sm" /> : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        Get Gold
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Elite Plan - Coming Soon */}
              <div 
                className={`stat-card p-8 relative transition-all border-white/10 opacity-80`}
                data-testid="elite-tier-card"
              >
                {/* Coming Soon Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold bg-zinc-700 text-zinc-300">
                  COMING SOON
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Sparkles className="w-8 h-8 text-cyan-400/50" />
                    <h3 className="font-heading font-bold text-2xl text-white/70">Elite</h3>
                  </div>
                  <p className="text-zinc-500 text-sm text-center mb-6">For serious sellers</p>
                  
                  <div className="text-center mb-6">
                    <p className="font-mono text-5xl font-bold text-cyan-400/50">
                      $45<span className="text-xl text-zinc-500">/mo</span>
                    </p>
                    <p className="text-zinc-400 text-sm mt-2">Billed monthly</p>
                  </div>

                  {/* Elite includes Gold */}
                  <div className="mb-4 p-2 rounded bg-amber-500/10 border border-amber-500/20 opacity-60">
                    <p className="text-amber-400/70 text-xs text-center">
                      <Crown className="w-3 h-3 inline mr-1" />
                      Includes everything in Gold, plus:
                    </p>
                  </div>

                  <div className="space-y-3 mb-8 opacity-60">
                    {eliteBenefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center"
                             style={{ backgroundColor: benefit.highlight ? 'rgba(0, 191, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)' }}>
                          <Check className="w-3 h-3" style={{ color: benefit.highlight ? '#00BFFF80' : '#a1a1aa80' }} />
                        </div>
                        <span className={`text-sm ${benefit.highlight ? 'text-cyan-300/50' : 'text-zinc-400'}`}>
                          {benefit.text}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Coming Soon Button */}
                  <div className="w-full h-12 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    <span className="text-zinc-400 font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Live Auctions Coming Soon
                    </span>
                  </div>
                  
                  <p className="text-zinc-500 text-xs text-center mt-4">
                    We're building dedicated servers for live auction streaming. Stay tuned!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Feature Comparison */}
          <div className="mt-12 stat-card p-6">
            <h2 className="font-heading font-bold text-xl text-white text-center mb-6">Feature Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 text-zinc-400">Feature</th>
                    <th className="text-center py-3 text-amber-400">
                      <Crown className="w-4 h-4 inline mr-1" />Gold
                    </th>
                    <th className="text-center py-3" style={{ color: '#00BFFF' }}>
                      <Sparkles className="w-4 h-4 inline mr-1" />Elite
                    </th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  <tr className="border-b border-white/5">
                    <td className="py-3">Unlimited Listings</td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3">0% Seller Commission</td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3">AI Card Scanner</td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3">Direct Messaging</td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 font-medium" style={{ color: '#00BFFF' }}>Host Live Auctions</td>
                    <td className="text-center text-zinc-600">—</td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 font-medium" style={{ color: '#00BFFF' }}>Combined Shipping</td>
                    <td className="text-center text-zinc-600">—</td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 font-medium" style={{ color: '#00BFFF' }}>Shipping Subsidy Protection</td>
                    <td className="text-center text-zinc-600">—</td>
                    <td className="text-center"><Check className="w-4 h-4 inline text-emerald-400" /></td>
                  </tr>
                  <tr>
                    <td className="py-3 font-bold text-white">Monthly Price</td>
                    <td className="text-center text-amber-400 font-mono font-bold">$20</td>
                    <td className="text-center font-mono font-bold" style={{ color: '#00BFFF' }}>$45</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-12 space-y-6 max-w-2xl mx-auto">
            <h2 className="font-heading font-bold text-2xl text-white text-center">FAQ</h2>
            
            <div className="stat-card p-6">
              <h3 className="font-medium text-white mb-2">What's the difference between Gold and Elite?</h3>
              <p className="text-zinc-400 text-sm">
                Gold members can list unlimited cards with 0% commission. Elite members get all Gold features 
                plus the ability to host live auctions, combined shipping discounts ($5 + $0.50/item), and 
                shipping subsidy protection where we cover overages.
              </p>
            </div>
            
            <div className="stat-card p-6">
              <h3 className="font-medium text-white mb-2">Can I upgrade from Gold to Elite?</h3>
              <p className="text-zinc-400 text-sm">
                Yes! You can upgrade anytime. Your new billing cycle will start immediately 
                with prorated credit for unused time on your Gold plan.
              </p>
            </div>
            
            <div className="stat-card p-6">
              <h3 className="font-medium text-white mb-2">What is Combined Shipping?</h3>
              <p className="text-zinc-400 text-sm">
                Elite members offer buyers discounted shipping on multi-item orders: $5.00 for the first item, 
                plus $0.50 for each additional item, capped at your max shipping fee. If actual shipping costs 
                more, we protect your payout with our subsidy system.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
